import { Icon } from "@iconify/react/dist/iconify.js";
import Column from "../Grid/Column";
import Container from "../Grid/Container";
import Row from "../Grid/Row";
import {
    ChangeEvent,
    FormEventHandler,
    useEffect,
    useRef,
    useState,
} from "react";
import { Link, router, useForm, usePage } from "@inertiajs/react";
import { SearchEngineData } from "@/types/searchEngineData";
import ReactSlider from "react-slider";
import { ApartmentModel } from "@/types/models/apartment";
import { PaginationLink } from "@/types/components/pagination/pagination";
import Pagination from "../Pagination/Pagination";
import { Transition } from "@headlessui/react";
import useIsServer from "@/Hooks/useIsServer";
import MainApartmentView from "./MainApartmentView";

const SearchEngine2D = ({
    searchEngineParams,
    apartments,
}: {
    searchEngineParams: SearchEngineData;
    apartments: {
        data: ApartmentModel[];
        links: PaginationLink[];
        total: number;
    };
}) => {
    const apartmentsRow = useRef<HTMLDivElement>(null);
    const [layoutType, setLayoutType] = useState<"grid" | "table">("grid");
    const isServer = useIsServer();
    const pageParams = usePage();

    const {
        minArea,
        maxArea,
        minRooms,
        maxRooms,
        minFloor,
        maxFloor,
        minPrice,
        maxPrice,
        garden,
        terrace,
        loggia,
        estateA,
        estateB,
        available,
    } = route().params;

    useEffect(() => {
        const { type } = route().params;

        if (type !== null && (type == "grid" || type == "table")) {
            // @ts-ignore
            setLayoutType(type);

            if (type === "table") {
                setTimeout(() => {
                    apartmentsRow.current?.scrollIntoView({
                        behavior: "smooth",
                        inline: "start",
                    });
                }, 100); // Small delay to ensure DOM is ready
            }
        }
    }, []);

    const { data, setData, get, processing } = useForm({
        minArea: minArea ? Number(minArea) : searchEngineParams.minArea,
        maxArea: maxArea ? Number(maxArea) : searchEngineParams.maxArea,
        minRooms: minRooms ? Number(minRooms) : searchEngineParams.minRooms,
        maxRooms: maxRooms ? Number(maxRooms) : searchEngineParams.maxRooms,
        minFloor: minFloor ? Number(minFloor) : searchEngineParams.minFloor,
        maxFloor: maxFloor ? Number(maxFloor) : searchEngineParams.maxFloor,
        minPrice: minPrice ? Number(minPrice) : searchEngineParams.minPrice,
        maxPrice: maxPrice ? Number(maxPrice) : searchEngineParams.maxPrice,
        garden: garden == "1" ? 1 : 0,
        terrace: terrace == "1" ? 1 : 0,
        loggia: loggia == "1" ? 1 : 0,
        estateA: estateA == "1" ? 1 : 0,
        estateB: estateB == "1" ? 1 : 0,
        available: available == "1" ? 1 : 0,
    });

    const [shiftedLabelLeft, setShiftedLabelLeft] = useState<boolean>(false);

    const handleSurfaceSliderChange = (value: number[], index: number) => {
        const [minValue, maxValue] = value;

        setData((data) => ({
            ...data,
            minArea: minValue,
            maxArea: maxValue,
        }));

        if (maxValue - minValue <= 25) {
            setShiftedLabelLeft(true);
        } else {
            setShiftedLabelLeft(false);
        }
    };

    const handleRoomsSliderChange = (value: number[], index: number) => {
        const [minValue, maxValue] = value;

        setData((data) => ({
            ...data,
            minRooms: minValue,
            maxRooms: maxValue,
        }));
    };

    const handleFloorSliderChange = (value: number[], index: number) => {
        const [minValue, maxValue] = value;

        setData((data) => ({
            ...data,
            minFloor: minValue,
            maxFloor: maxValue,
        }));
    };

    const handlePriceSliderChange = (value: number[], index: number) => {
        const [minValue, maxValue] = value;

        setData((data) => ({
            ...data,
            minPrice: minValue,
            maxPrice: maxValue,
        }));
    };

    const handleApartmentSearch: FormEventHandler = (e) => {
        e.preventDefault();
        const queryParams = {
            type: route().params.type,
            variation: route().params.variation,
        };

        get(
            route("frontend.apartments", {
                _query: {
                    ...route().params,
                },
            }),
            {
                preserveScroll: true,
                preserveState: true,
                onFinish: () => {
                    if (!isServer) {
                        const targetElement =
                            document.getElementById("found-apartments");

                        targetElement?.scrollIntoView({
                            behavior: "smooth",
                            inline: "start",
                        });
                    }
                },
            },
        );
    };

    const getFormat = (filename: string) => {
        return filename.split(".").pop();
    };

    const resolveStatusBadge = (status: string) => {
        switch (status) {
            case "Wolne":
                return {
                    label: "Dostępne",
                    className: "bg-[#1E7200] border-[#1E7200] text-white",
                };
            case "Zarezerwowane":
            case "Wstępnie Zarezerwowany":
            case "WstepnieZarezerwowany":
                return {
                    label: "Zarezerwowane",
                    className: "bg-i-red border-i-red text-white",
                };
            case "Sprzedane":
                return {
                    label: "Sprzedane",
                    className: "bg-text border-text text-white",
                };
            default:
                return {
                    label: status,
                    className: "bg-secondary border-secondary text-white",
                };
        }
    };

    const goToTopApartments = () => {
        if (apartmentsRow.current) {
            apartmentsRow.current.scrollIntoView({
                behavior: "smooth",
            });
        }
    };

    const resolveStatusType = (status: string) => {
        let formattedStatus = <span></span>;

        switch (status) {
            case "Zarezerwowane":
                formattedStatus = (
                    <span className="text-i-red">Zarezerwowane</span>
                );
                break;
            case "WstepnieZarezerwowany":
            case "Wstępnie Zarezerwowany":
                formattedStatus = (
                    <span className="text-i-red">Zarezerwowane</span>
                );
                break;
            case "Wolne":
                formattedStatus = (
                    <span className="text-[#1E7200]">Dostępne</span>
                );
                break;
            case "Sprzedane":
                formattedStatus = <span className="text-text">Sprzedane</span>;
                break;
        }

        return formattedStatus;
    };

    const handleLayoutDataChange = (type: "grid" | "table") => {
        setLayoutType(type);

        const queryString = route().params;
        const ziggy = pageParams.props.ziggy as {
            location: string;
            query: { [key: string]: string };
        };

        if (!isServer) {
            router.visit(
                route("frontend.apartments", {
                    _query: {
                        ...queryString,
                        type: type,
                    },
                }),
                {
                    preserveScroll: true,
                    preserveState: true,
                },
            );
        }
    };

    const handleOrderTable = (field: string) => {
        const queryString = route().params;
        const ziggy = pageParams.props.ziggy as {
            location: string;
            query: { [key: string]: string };
        };

        if (!isServer) {
            router.visit(
                route("frontend.apartments", {
                    _query: {
                        ...queryString,
                        field: field,
                        order: queryString.order === "asc" ? "desc" : "asc",
                    },
                }),
                {
                    preserveScroll: true,
                    preserveState: true,
                },
            );
        }
    };

    return (
        <section>
            <Container>
                <Row>
                    <Column>
                        <div className="relative">
                            <p className="text-p leading-p text-primary uppercase font-bold maxMd:block hidden text-center mb-4">
                                Wybierz piętro na mapie
                            </p>

                            <MainApartmentView />
                        </div>
                    </Column>
                </Row>
                <Row>
                    <Column>
                        <h3 className="text-primary text-h3 uppercase leading-h3 font-bold text-center mb-10 mt-10 maxMd:mt-8 maxMd:mb-6">
                            Znajdź swoje wymarzone mieszkanie
                        </h3>
                    </Column>
                </Row>
                <Row>
                    <Column>
                        <div className="flex flex-wrap justify-center items-center gap-x-6 mb-14">
                            <Link
                                href={route("frontend.apartments", {
                                    variation: "3d",
                                })}
                                className="maxMd:w-full maxMd:mb-6 button-outline border-primary border-[2px]"
                            >
                                Wyszukiwarka 3D
                            </Link>
                            <button
                                type="button"
                                className="maxMd:w-full button-primary border-primary border-[2px]"
                            >
                                Wyszukiwarka filtry
                            </button>
                        </div>
                    </Column>
                </Row>
                <Row className="justify-center mb-14">
                    <Column>
                        <form
                            className="filter-area bg-white py-8 px-10 rounded-2xl flex flex-wrap items-end gap-x-8 gap-y-8 shadow-2xl"
                            onSubmit={handleApartmentSearch}
                        >
                            <div className="max-w-[18%] w-full maxMd:max-w-[100%]">
                                <p className="text-label leading-label text-primary uppercase font-extrabold mb-4">
                                    Powierzchnia
                                </p>
                                <ReactSlider
                                    className="apartments-range-slider"
                                    thumbClassName="apartments-range-slider-thumb"
                                    trackClassName="apartments-range-slider-track"
                                    defaultValue={[
                                        minArea
                                            ? Number(minArea)
                                            : Math.floor(
                                                searchEngineParams.minArea,
                                            ),
                                        maxArea
                                            ? Number(maxArea)
                                            : Math.ceil(
                                                searchEngineParams.maxArea,
                                            ),
                                    ]}
                                    min={Math.floor(searchEngineParams.minArea)}
                                    max={Math.ceil(searchEngineParams.maxArea)}
                                    ariaLabel={[
                                        "Miminalna powierzchnia suwak",
                                        "Maksymalna powierzchnia suwak",
                                    ]}
                                    renderThumb={(props, state) => (
                                        <div key={state.index + "area"}>
                                            <div
                                                {...props}
                                                className={`tracking-label-slide tracking-label-side-${state.index} text-primary font-extrabold text-label leading-label uppercase whitespace-pre`}
                                            >
                                                <div
                                                    className={`h-4 w-4 rounded-full bg-secondary -mt-[6px] ${state.index == 1 ? "ml-auto" : ""}`}
                                                ></div>
                                            </div>
                                            <p
                                                className={`mt-2 absolute bottom-2 ${state.index === 1 ? "right-0" : ""} text-primary font-extrabold text-label leading-label uppercase whitespace-pre`}
                                            >
                                                {state.valueNow} M<sup>2</sup>
                                            </p>
                                        </div>
                                    )}
                                    step={1}
                                    onChange={handleSurfaceSliderChange}
                                />
                            </div>
                            <div className="max-w-[18%] w-full maxMd:max-w-[100%]">
                                <p className="text-label leading-label text-primary uppercase font-extrabold mb-4">
                                    Liczba pokoi
                                </p>
                                <ReactSlider
                                    className="apartments-range-slider"
                                    thumbClassName="apartments-range-slider-thumb"
                                    trackClassName="apartments-range-slider-track"
                                    defaultValue={[
                                        minRooms
                                            ? Number(minRooms)
                                            : searchEngineParams.minRooms,
                                        maxRooms
                                            ? Number(maxRooms)
                                            : searchEngineParams.maxRooms,
                                    ]}
                                    min={searchEngineParams.minRooms}
                                    max={searchEngineParams.maxRooms}
                                    onChange={handleRoomsSliderChange}
                                    ariaLabel={[
                                        "Miminalna ilość pokoi suwak",
                                        "Maksymalna ilość pokoi suwak",
                                    ]}
                                    renderThumb={(props, state) => (
                                        <div key={state.index + "rooms"}>
                                            <div
                                                {...props}
                                                className={`tracking-label-slide tracking-label-side-${state.index} text-primary font-extrabold text-label leading-label uppercase whitespace-pre`}
                                            >
                                                <div
                                                    className={`h-4 w-4 rounded-full bg-secondary -mt-[6px] ${state.index == 1 ? "ml-auto" : ""}`}
                                                ></div>
                                            </div>
                                            <p
                                                className={`mt-2 absolute bottom-2 ${state.index === 1 ? "right-0" : ""} text-primary font-extrabold text-label leading-label uppercase whitespace-pre`}
                                            >
                                                {state.valueNow}
                                            </p>
                                        </div>
                                    )}
                                    step={1}
                                />
                            </div>
                            <div className="max-w-[18%] w-full maxMd:max-w-[100%]">
                                <p className="text-label leading-label text-primary uppercase font-extrabold mb-4">
                                    Piętro
                                </p>
                                <ReactSlider
                                    className="apartments-range-slider"
                                    thumbClassName="apartments-range-slider-thumb"
                                    trackClassName="apartments-range-slider-track"
                                    defaultValue={[
                                        minFloor
                                            ? Number(minFloor)
                                            : searchEngineParams.minFloor,
                                        maxFloor
                                            ? Number(maxFloor)
                                            : searchEngineParams.maxFloor,
                                    ]}
                                    min={searchEngineParams.minFloor}
                                    max={searchEngineParams.maxFloor}
                                    onChange={handleFloorSliderChange}
                                    ariaLabel={[
                                        "Miminalne piętro suwak",
                                        "Maksymalne piętro suwak",
                                    ]}
                                    renderThumb={(props, state) => (
                                        <div key={state.index + "floor"}>
                                            <div
                                                {...props}
                                                className={`tracking-label-slide tracking-label-side-${state.index} text-primary font-extrabold text-label leading-label uppercase whitespace-pre`}
                                            >
                                                <div
                                                    className={`h-4 w-4 rounded-full bg-secondary -mt-[6px] ${state.index == 1 ? "ml-auto" : ""}`}
                                                ></div>
                                            </div>
                                            <p
                                                className={`mt-2 absolute bottom-2 ${state.index === 1 ? "right-0" : ""} text-primary font-extrabold text-label leading-label uppercase whitespace-pre`}
                                            >
                                                {state.valueNow}
                                            </p>
                                        </div>
                                    )}
                                    step={1}
                                />
                            </div>
                            <div className="max-w-[18%] w-full maxMd:max-w-[100%]">
                                <p className="text-label leading-label text-primary uppercase font-extrabold mb-4">
                                    Cena
                                </p>
                                <ReactSlider
                                    className="apartments-range-slider"
                                    thumbClassName="apartments-range-slider-thumb"
                                    trackClassName="apartments-range-slider-track"
                                    defaultValue={[
                                        minPrice
                                            ? Number(minPrice)
                                            : Math.floor(searchEngineParams.minPrice),
                                        maxPrice
                                            ? Number(maxPrice)
                                            : Math.ceil(searchEngineParams.maxPrice),
                                    ]}
                                    min={Math.floor(searchEngineParams.minPrice)}
                                    max={Math.ceil(searchEngineParams.maxPrice)}
                                    onChange={handlePriceSliderChange}
                                    ariaLabel={[
                                        "Minimalna cena suwak",
                                        "Maksymalna cena suwak",
                                    ]}
                                    renderThumb={(props, state) => (
                                        <div key={state.index + "price"}>
                                            <div
                                                {...props}
                                                className={`tracking-label-slide tracking-label-side-${state.index} text-primary font-extrabold text-label leading-label uppercase whitespace-pre`}
                                            >
                                                <div
                                                    className={`h-4 w-4 rounded-full bg-secondary -mt-[6px] ${state.index == 1 ? "ml-auto" : ""}`}
                                                ></div>
                                            </div>
                                            <p
                                                className={`mt-2 absolute bottom-2 ${state.index === 1 ? "right-0" : ""} text-primary font-extrabold text-[11px] leading-label uppercase whitespace-pre`}
                                            >
                                                {new Intl.NumberFormat("pl-PL", {
                                                    notation: "compact",
                                                    maximumFractionDigits: 1,
                                                }).format(state.valueNow)}{" "}
                                                zł
                                            </p>
                                        </div>
                                    )}
                                    step={1000}
                                />
                            </div>
                            <div className="ml-auto maxMd:ml-0 maxMd:w-full">
                                <button
                                    type="submit"
                                    disabled={processing ? true : false}
                                    className="button-primary maxMd:w-full py-3"
                                >
                                    Szukaj
                                </button>
                            </div>
                            <div className="w-full mt-10 gap-x-22 flex flex-wrap items-center justify-center maxMd:pl-6">
                                <div className="mr-auto flex relative maxMd:w-full gap-x-22 flex-wrap items-center justify-center pl-6 maxMd:pl-0 gap-y-6 maxMd:mb-6">
                                    <div className="relative maxMd:w-full">
                                        <input
                                            type="checkbox"
                                            name="estateA"
                                            id="estateA"
                                            className="hidden filter-apartment-checkbox"
                                            checked={
                                                data.estateA == 1 ? true : false
                                            }
                                            onChange={(
                                                e: ChangeEvent<HTMLInputElement>,
                                            ) =>
                                                setData(
                                                    "estateA",
                                                    e.currentTarget.checked
                                                        ? 1
                                                        : 0,
                                                )
                                            }
                                        />
                                        <label
                                            htmlFor="estateA"
                                            className="text-text cursor-pointer text-label leading-label font-extrabold uppercase pl-4 filter-apartment-label"
                                        >
                                            Budynek A
                                        </label>
                                    </div>
                                    <div className="relative maxMd:w-full">
                                        <input
                                            type="checkbox"
                                            name="estateB"
                                            id="estateB"
                                            className="hidden filter-apartment-checkbox"
                                            checked={
                                                data.estateB == 1 ? true : false
                                            }
                                            onChange={(
                                                e: ChangeEvent<HTMLInputElement>,
                                            ) =>
                                                setData(
                                                    "estateB",
                                                    e.currentTarget.checked
                                                        ? 1
                                                        : 0,
                                                )
                                            }
                                        />
                                        <label
                                            htmlFor="estateB"
                                            className="text-text cursor-pointer text-label leading-label font-extrabold uppercase pl-4 filter-apartment-label"
                                        >
                                            Budynek B
                                        </label>
                                    </div>
                                    <div className="relative maxMd:w-full">
                                        <input
                                            type="checkbox"
                                            name="available"
                                            id="available"
                                            className="hidden filter-apartment-checkbox"
                                            checked={
                                                data.available == 1 ? true : false
                                            }
                                            onChange={(
                                                e: ChangeEvent<HTMLInputElement>,
                                            ) =>
                                                setData(
                                                    "available",
                                                    e.currentTarget.checked
                                                        ? 1
                                                        : 0,
                                                )
                                            }
                                        />
                                        <label
                                            htmlFor="available"
                                            className="text-text cursor-pointer text-label leading-label font-extrabold uppercase pl-4 filter-apartment-label"
                                        >
                                            Tylko dostępne
                                        </label>
                                    </div>
                                </div>
                                <div className="relative maxMd:w-full maxMd:mb-6">
                                    <input
                                        type="checkbox"
                                        name="garden"
                                        id="garden"
                                        className="hidden filter-apartment-checkbox"
                                        checked={
                                            data.garden == 1 ? true : false
                                        }
                                        onChange={(
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) =>
                                            setData(
                                                "garden",
                                                e.currentTarget.checked ? 1 : 0,
                                            )
                                        }
                                    />
                                    <label
                                        htmlFor="garden"
                                        className="text-text cursor-pointer text-label leading-label font-extrabold uppercase pl-4 filter-apartment-label"
                                    >
                                        Ogród
                                    </label>
                                </div>
                                <div className="relative maxMd:w-full maxMd:mb-6">
                                    <input
                                        type="checkbox"
                                        name="terrace"
                                        id="terrace"
                                        className="hidden filter-apartment-checkbox"
                                        checked={
                                            data.terrace == 1 ? true : false
                                        }
                                        onChange={(
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) =>
                                            setData(
                                                "terrace",
                                                e.currentTarget.checked ? 1 : 0,
                                            )
                                        }
                                    />
                                    <label
                                        htmlFor="terrace"
                                        className="text-text cursor-pointer text-label leading-label font-extrabold uppercase pl-4 filter-apartment-label"
                                    >
                                        Taras
                                    </label>
                                </div>
                                <div className="relative maxMd:w-full">
                                    <input
                                        type="checkbox"
                                        name="loggia"
                                        id="loggia"
                                        className="hidden filter-apartment-checkbox"
                                        checked={
                                            data.loggia == 1 ? true : false
                                        }
                                        onChange={(
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) =>
                                            setData(
                                                "loggia",
                                                e.currentTarget.checked ? 1 : 0,
                                            )
                                        }
                                    />
                                    <label
                                        htmlFor="loggia"
                                        className="text-text cursor-pointer text-label leading-label font-extrabold uppercase pl-4 filter-apartment-label"
                                    >
                                        Balkon/Loggia
                                    </label>
                                </div>
                            </div>
                        </form>
                    </Column>
                </Row>
                <Row>
                    <Column>
                        <div
                            id="found-apartments"
                            className="flex justify-between px-9 mb-6 maxMd:px-0 maxMd:items-start maxMd:mb-8"
                        >
                            {apartments.total > 0 ? (
                                <p className="text-text text-p leading-p">
                                    <b>Znaleziono</b>{" "}
                                    <b className="text-primary">
                                        {apartments.total}
                                    </b>{" "}
                                    <span className="maxMd:block">
                                        mieszkania
                                    </span>{" "}
                                    <span className="maxMd:hidden">
                                        dopasowane do Ciebie
                                    </span>
                                </p>
                            ) : (
                                <p className="text-text text-p leading-p">
                                    <b>Znaleziono</b>{" "}
                                    <b className="text-primary">
                                        {apartments.total}
                                    </b>{" "}
                                    <span className="maxMd:block">
                                        mieszkania
                                    </span>{" "}
                                    <span className="maxMd:hidden">
                                        dopasowane do Ciebie
                                    </span>
                                </p>
                            )}
                            <div className="flex items-center gap-x-8 maxMd:mt-2">
                                <button
                                    type="button"
                                    onClick={() =>
                                        handleLayoutDataChange("grid")
                                    }
                                >
                                    <Icon
                                        icon={`bi:grid`}
                                        width={32}
                                        height={32}
                                        className={`${layoutType === "grid" ? "text-primary" : "text-secondary"}`}
                                    />
                                </button>
                                <button
                                    type="button"
                                    onClick={() =>
                                        handleLayoutDataChange("table")
                                    }
                                >
                                    <Icon
                                        icon={`ph:rows`}
                                        width={32}
                                        height={32}
                                        className={`${layoutType === "table" ? "text-primary" : "text-secondary"}`}
                                    />
                                </button>
                            </div>
                        </div>
                    </Column>
                </Row>
                {apartments.total === 0 && (
                    <Row className="justify-center">
                        <Column xl={6}>
                            <h4 className="text-h3 leading-h3 text-primary font-extrabold text-center mb-8">
                                Nie znaleźliśmy rozkładu pasującego do wybranych
                                parametrów
                            </h4>
                            <img
                                src="/storage/icons/not-found-home.svg"
                                alt="Nie znaleziono mieszkania - ikona"
                                width={200}
                                height={200}
                                className="mx-auto mb-8"
                            />
                            <p className="text-p leading-p text-text mb-4 maxMd:text-center">
                                Nie przejmuj się! Mamy w ofercie wiele innych
                                rozkładów, które spełnią Twoje oczekiwania.{" "}
                            </p>
                            <p className="text-h4 leading-h4 text-text text-center mb-24 maxMd:mb-14 maxMd:text-p maxMd:leading-p">
                                <span className="text-primary">
                                    Skontaktuj się z nami.
                                </span>{" "}
                                Jesteśmy pewni, że razem znajdziemy Twoje nowe
                                mieszkanie
                            </p>
                        </Column>
                    </Row>
                )}
                <Row ref={apartmentsRow}>
                    {layoutType === "grid" &&
                        apartments.data &&
                        apartments.data.map((apartment) => {
                            const status = resolveStatusBadge(apartment.status);
                            const floorCount =
                                Math.max(
                                    Number(searchEngineParams.maxFloor) || 0,
                                    Number(apartment.floor) || 0,
                                    10,
                                ) + 1;
                            const detailsUrl = route(
                                "frontend.apartments.details",
                                { apartment: apartment.api_id },
                            );
                            const isHighlighted =
                                apartment.api_id ==
                                "32a293ec-3582-ee11-8179-000d3a2e47bc";

                            return (
                                <Column
                                    xl={3}
                                    lg={4}
                                    md={6}
                                    key={apartment.api_id + apartment.name}
                                    className="mb-6"
                                >
                                    <div
                                        className={`bg-white border rounded-lg overflow-hidden flex flex-col h-full ${isHighlighted
                                            ? "border-[8px] border-alert"
                                            : "border-secondary"
                                            }`}
                                    >
                                        <div className="flex items-stretch w-full">
                                            <div className="border-r border-secondary flex flex-col items-center shrink-0 w-[86px]">
                                                <p className="text-p leading-p text-text font-extrabold tracking-p text-center pt-4 pb-2">
                                                    Piętro
                                                </p>
                                                <div className="flex flex-col items-center gap-3 pt-4 pb-10 w-full">
                                                    {Array.from(
                                                        { length: floorCount },
                                                        (_, i) => (
                                                            <p
                                                                key={i}
                                                                className={`text-p leading-p tracking-p text-center size-8 flex items-center justify-center rounded-full ${i ===
                                                                    Number(
                                                                        apartment.floor,
                                                                    )
                                                                    ? "text-primary border border-secondary shadow-[0px_4px_4px_rgba(0,64,83,0.15)]"
                                                                    : "text-text"
                                                                    }`}
                                                            >
                                                                {i}
                                                            </p>
                                                        ),
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex flex-col flex-1 min-w-0 self-stretch">
                                                <div className="flex flex-col gap-2 p-4 shrink-0">
                                                    <p className="text-h4 leading-h4 text-text tracking-[0.6px] uppercase">
                                                        Mieszkanie{" "}
                                                        {apartment.name}
                                                    </p>
                                                    <span
                                                        className={`inline-flex items-center justify-center px-4 py-3 rounded-[32px] border text-4 leading-4 font-extrabold tracking-[0.35px] uppercase w-fit ${status.className}`}
                                                    >
                                                        {status.label}
                                                    </span>
                                                    <div className="flex flex-col">
                                                        {apartment.status !==
                                                            "Sprzedane" && (
                                                                <p className="text-p leading-p tracking-p text-primary">
                                                                    Cena:{" "}
                                                                    <span className="text-text">
                                                                        {new Intl.NumberFormat(
                                                                            "pl-PL",
                                                                        ).format(
                                                                            apartment.priceforapartment,
                                                                        )}{" "}
                                                                        zł
                                                                    </span>
                                                                </p>
                                                            )}
                                                        <p className="text-p leading-p tracking-p text-primary">
                                                            Powierzchnia:{" "}
                                                            <span className="text-text">
                                                                {apartment.area}{" "}
                                                                m<sup>2</sup>
                                                            </span>
                                                        </p>
                                                        <p className="text-p leading-p tracking-p text-primary">
                                                            Liczba pokoi:{" "}
                                                            <span className="text-text">
                                                                {
                                                                    apartment.numberofrooms
                                                                }
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>

                                                {apartment.get_attachment
                                                    ?.layout ? (
                                                    <div className="relative flex-1 min-h-[220px] w-full">
                                                        <div className="absolute inset-0 flex items-center justify-center p-3">
                                                            <picture className="flex h-full max-h-full max-w-full items-center justify-center">
                                                                <source
                                                                    srcSet={`${apartment.get_attachment.layout.replace(/\.[^.]+$/, "")}.webp`}
                                                                    type="image/webp"
                                                                />
                                                                <source
                                                                    srcSet={`${apartment.get_attachment.layout.replace(/\.[^.]+$/, "")}.${getFormat(apartment.get_attachment.layout)}`}
                                                                    type={`image/${getFormat(apartment.get_attachment.layout)}`}
                                                                />
                                                                <img
                                                                    className="max-h-full max-w-full object-contain"
                                                                    src={
                                                                        apartment
                                                                            .get_attachment
                                                                            .layout
                                                                    }
                                                                    alt={`Rzut mieszkania ${apartment.name}`}
                                                                    loading="lazy"
                                                                />
                                                            </picture>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="relative flex-1 min-h-[220px] w-full">
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <Icon
                                                                icon="clarity:image-line"
                                                                width={120}
                                                                className="text-secondary"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex gap-4 items-start justify-center px-4 py-4">
                                            <a
                                                href={`${detailsUrl}#contact-form`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    window.location.href = `${detailsUrl}#contact-form`;
                                                }}
                                                className="button-small bg-secondary border-secondary text-white text-center px-6 py-3 leading-[16px]"
                                            >
                                                Zapytaj o to
                                                <br />
                                                mieszkanie
                                            </a>
                                            {apartment.get_attachment
                                                ?.apartment_card ? (
                                                <a
                                                    href={
                                                        apartment
                                                            .get_attachment
                                                            .apartment_card
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="button-small bg-[#39A7C8] border-[#39A7C8] text-white text-center px-6 py-3 leading-[16px]"
                                                >
                                                    Pobierz
                                                    <br />
                                                    kartę lokalu
                                                </a>
                                            ) : (
                                                <span className="button-small bg-[#39A7C8]/60 border-[#39A7C8]/60 text-white text-center px-6 py-3 leading-[16px] opacity-50 cursor-not-allowed">
                                                    Pobierz
                                                    <br />
                                                    kartę lokalu
                                                </span>
                                            )}
                                        </div>

                                        <div className="bg-secondary h-px opacity-50 w-full" />

                                        <a
                                            href={detailsUrl}
                                            target="_blank"
                                            className="flex gap-2 items-center justify-center px-4 py-5 text-primary text-p leading-label font-bold tracking-p uppercase"
                                        >
                                            Szczegóły
                                            <Icon
                                                icon="cil:magnifying-glass"
                                                width={24}
                                                height={24}
                                            />
                                        </a>
                                    </div>
                                </Column>
                            );
                        })}
                    {layoutType === "table" && apartments.total > 0 && (
                        <div className="overflow-x-scroll w-full">
                            <table className="table-auto overflow-scroll w-full table-apartments">
                                <thead>
                                    <tr>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleOrderTable("name")
                                                }
                                            >
                                                MIESZKANIE
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleOrderTable("floor")
                                                }
                                            >
                                                PIĘTRO
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleOrderTable(
                                                        "numberofrooms",
                                                    )
                                                }
                                            >
                                                L. POK.
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleOrderTable("area")
                                                }
                                            >
                                                POW. M2
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleOrderTable(
                                                        "priceforapartment",
                                                    )
                                                }
                                            >
                                                CENA
                                            </button>
                                        </td>
                                        {/* <td>
                                            <button type="button">
                                                CENA PROMOCYJNA
                                            </button>
                                        </td> */}
                                        {/* <td>
                                            <button type="button" onClick={() => handleOrderTable('balconysize')}>
                                                BALKON M2
                                            </button>
                                        </td> */}
                                        {/* <td>
                                            <button type="button" onClick={() => handleOrderTable('loggiasize')}>
                                                LOGGIA M2
                                            </button>
                                        </td>
                                        <td>
                                            <button type="button" onClick={() => handleOrderTable('terracesize')}>
                                                TARAS M2
                                            </button>
                                        </td>
                                        <td>
                                            <button type="button" onClick={() => handleOrderTable('gardensize')}>
                                                OGRÓD M2
                                            </button>
                                        </td> */}
                                        {/* <td>
                                            <button type="button" onClick={() => handleOrderTable('priceforapartment')}>
                                                CENA
                                            </button>
                                        </td> */}
                                        <td>RZUT</td>
                                        <td>RZUT PDF</td>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleOrderTable("status")
                                                }
                                            >
                                                STATUS
                                            </button>
                                        </td>
                                        <td></td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {apartments.data &&
                                        apartments.data.map((apartment, id) => (
                                            <tr
                                                key={
                                                    apartment.api_id +
                                                    id +
                                                    apartment.id
                                                }
                                            >
                                                <td>{apartment.name}</td>
                                                <td>{apartment.floor}</td>
                                                <td>
                                                    {apartment.numberofrooms}
                                                </td>
                                                <td>{apartment.area}</td>
                                                <td>
                                                    <span>
                                                        {new Intl.NumberFormat(
                                                            "pl-PL",
                                                        ).format(
                                                            apartment.priceforapartment,
                                                        )}{" "}
                                                        zł
                                                    </span>
                                                </td>
                                                <td>
                                                    {apartment.get_attachment ? (
                                                        <a
                                                            target="_blank"
                                                            className="text-primary"
                                                            href={
                                                                apartment
                                                                    .get_attachment
                                                                    ?.layout
                                                            }
                                                        >
                                                            RZUT
                                                        </a>
                                                    ) : (
                                                        <span>BRAK</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {apartment.get_attachment ? (
                                                        <a
                                                            target="_blank"
                                                            className="text-primary"
                                                            href={
                                                                apartment
                                                                    .get_attachment
                                                                    ?.apartment_card
                                                            }
                                                        >
                                                            RZUT PDF
                                                        </a>
                                                    ) : (
                                                        <span>BRAK</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {resolveStatusType(
                                                        apartment.status,
                                                    )}
                                                </td>
                                                <td>
                                                    <Link
                                                        href={route(
                                                            "frontend.apartments.yslab.details",
                                                            {
                                                                apartmentName:
                                                                    apartment.name.replace(
                                                                        "/",
                                                                        "_",
                                                                    ),
                                                            },
                                                        )}
                                                        className="button-small"
                                                    >
                                                        Szczegóły
                                                    </Link>
                                                    {/* <a className="button-primary max-w-[260px] w-full border-primary border-[2px]" href={route('frontend.apartments.details', { api_id: apartment.api_id })} target="_blank" title={`Sprawdź mieszkanie ${apartment.name}`}>
                                                    ZAPYTAJ
                                                </a> */}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Row>
                {apartments.total > 0 && (
                    <Row className="mb-6 justify-between items-center maxMd:mb-6">
                        <Column xl={4}>
                            <button
                                type="button"
                                className="button-outline"
                                onClick={goToTopApartments}
                            >
                                Powrót na górę listy
                                <Icon
                                    icon={`streamline:return-2`}
                                    className="ml-1"
                                />
                            </button>
                        </Column>
                        {apartments.links && (
                            <Column xl={6}>
                                <Pagination
                                    preserveScroll
                                    className="justify-end"
                                    links={apartments.links}
                                />
                            </Column>
                        )}
                    </Row>
                )}
            </Container>
        </section>
    );
};

export default SearchEngine2D;
